import { Landmark, MousePointerClick, QrCode, Smartphone } from "lucide-react";
import { type ReactNode, useEffect, useId } from "react";

import { PayinRail } from "@/modules/dashboard/payins/services/paymentsPayin.service";
import { ColocaChoiceCards } from "@/shared/components/forms/ColocaChoiceCards";
import { ColocaLabel } from "@/shared/components/forms/ColocaLabel";
import type { ColombiaPayinMethod } from "@/shared/components/payments/colombiaPayinMethods";
import { useAvailableCorridors } from "@/shared/hooks/useAvailableCorridors";
import useTranslations from "@/shared/lang/useTranslations";
import { formatLimitAmount } from "@/shared/utils/amountLimits";
import { formatRail } from "@/shared/utils/railDisplay";

export interface ColombiaPayinMethodChoiceProps {
  value: ColombiaPayinMethod;
  onChange: (method: ColombiaPayinMethod) => void;
  disabled?: boolean;
  /**
   * The rails the CALLER can actually execute — not which ones are up, which is resolved here.
   *
   * Required, and deliberately so. This component used to offer all four to everyone, and `/app/payins` only
   * builds two payloads: choosing Nequi or Botón Bancolombia there created the payin as Bre-B, priced off the
   * wrong fee row and recorded under the wrong rail, with nothing anywhere reporting an error. A card that a
   * surface cannot honour is worse than a missing one, and nothing in the type system was stopping it. Now
   * adding a fifth rail here breaks the build at every call site until each one says whether it supports it.
   *
   * Prefer a module-level constant over an inline literal: this is an effect dependency.
   */
  methods: readonly ColombiaPayinMethod[];
}

/**
 * The Colombian collection methods, as cards that say what each one actually does to the payer.
 *
 * The choice is component state — it decides which step comes next, not a form field — so this wraps the
 * form-agnostic `ColocaChoiceCards`. It carries the copy so `/app/payins` and the wallet deposit modal cannot
 * describe the same rails differently.
 *
 * Availability is resolved here rather than passed in: each rail is a corridor of its own, any can lose its
 * route or its fees independently, and a caller that forgot to check would offer a card that fails with
 * `no_route` at create time. An unavailable rail renders disabled and says why — hiding it would read as a
 * product that never had it. The corridor catalog fails open, so an unreadable one disables nothing.
 *
 * `methods` is the other axis and means something different: not "is this rail up" but "can the caller
 * execute it". A rail the caller cannot build a payload for is omitted entirely rather than disabled — there
 * is no "try later" to offer, the surface simply does not do it.
 */
export function ColombiaPayinMethodChoice({
  value,
  onChange,
  disabled,
  methods,
}: ColombiaPayinMethodChoiceProps) {
  const { t, activeLanguage } = useTranslations("payins");
  const labelId = useId();
  const corridors = useAvailableCorridors();

  // The cap is part of choosing a method: a payer over 3.5M has to know PSE will not take it as a person
  // before filling in the payer step. Both kinds are shown when they differ, since the kind is not known yet.
  const capLine = (method: ColombiaPayinMethod): string | null => {
    const limits = corridors.limitsFor(method, "COL", "COP", "PAYIN");
    if (!limits) return null;
    const format = (value: string) => formatLimitAmount(value, "COP", activeLanguage);
    if (limits.differs && limits.person.max_amount && limits.company.max_amount) {
      return t("method_limit_up_to_by_payer", {
        person: format(limits.person.max_amount),
        company: format(limits.company.max_amount),
      });
    }
    return limits.any.max_amount ? t("method_limit_up_to", { max: format(limits.any.max_amount) }) : null;
  };
  const describe = (
    method: ColombiaPayinMethod,
    key: "method_breb_desc" | "method_pse_desc" | "method_nequi_desc" | "method_bancolombia_desc",
  ): ReactNode => {
    if (!available[method]) return unavailableCopy(method);
    const cap = capLine(method);
    return cap ? (
      <>
        {t(key)}
        <span className="mt-1 block font-mono text-[11px] text-foreground/80">{cap}</span>
      </>
    ) : (
      t(key)
    );
  };

  const brebAvailable = corridors.isAvailable(PayinRail.BREB, "COL", "COP", "PAYIN");
  const pseAvailable = corridors.isAvailable(PayinRail.PSE, "COL", "COP", "PAYIN");
  const nequiAvailable = corridors.isAvailable(PayinRail.NEQUI, "COL", "COP", "PAYIN");
  const bancolombiaAvailable = corridors.isAvailable(PayinRail.BANCOLOMBIA_BUTTON, "COL", "COP", "PAYIN");
  const available: Record<ColombiaPayinMethod, boolean> = {
    [PayinRail.BREB]: brebAvailable,
    [PayinRail.PSE]: pseAvailable,
    [PayinRail.NEQUI]: nequiAvailable,
    [PayinRail.BANCOLOMBIA_BUTTON]: bancolombiaAvailable,
  };
  const selectedAvailable = available[value];

  // A switched-off rail is temporary ("try later"); a missing fee config is a state of the account
  // ("talk to your executive") — two different next actions, so two different lines.
  const unavailableCopy = (method: ColombiaPayinMethod): string =>
    corridors.reasonFor(method, "COL", "COP", "PAYIN") === "rail_disabled"
      ? t("method_temporarily_disabled")
      : t("method_unavailable");

  // Landing on a rail that happens to be down is a dead end: the card is disabled and the step's action
  // stays gated with nothing the user can do about it. Move to the first one that works, once.
  useEffect(() => {
    if (selectedAvailable) return;
    const availability: Record<ColombiaPayinMethod, boolean> = {
      [PayinRail.BREB]: brebAvailable,
      [PayinRail.PSE]: pseAvailable,
      [PayinRail.NEQUI]: nequiAvailable,
      [PayinRail.BANCOLOMBIA_BUTTON]: bancolombiaAvailable,
    };
    // Only among the rails this caller can execute — falling back to one it cannot build is the defect
    // this component is being fixed for, arrived at by a different door.
    const fallback = methods.find((method) => availability[method]);
    if (fallback) onChange(fallback);
  }, [
    selectedAvailable,
    brebAvailable,
    pseAvailable,
    nequiAvailable,
    bancolombiaAvailable,
    methods,
    onChange,
  ]);

  return (
    <div className="space-y-2">
      <ColocaLabel id={labelId}>{t("method_choice_label")}</ColocaLabel>
      <ColocaChoiceCards
        aria-labelledby={labelId}
        value={value}
        onValueChange={(next) => onChange(next as ColombiaPayinMethod)}
        disabled={disabled}
        columns={2}
        // Two columns on a 360px phone give each card ~158px for an icon, a title, a description and a cap
        // line, which stacks them far taller than they need to be and is what pushed this block past the
        // panel. One column below `sm`; `columns={2}` still governs from there up, and the consumer's
        // className is merged last so it wins over its `grid-cols-2`.
        className="grid-cols-1 sm:grid-cols-2"
        items={[
          {
            value: PayinRail.BREB,
            label: formatRail("BREB"),
            description: describe(PayinRail.BREB, "method_breb_desc"),
            icon: <QrCode className="size-5" aria-hidden="true" />,
            disabled: !available[PayinRail.BREB],
          },
          {
            value: PayinRail.PSE,
            label: t("method_pse_title"),
            description: describe(PayinRail.PSE, "method_pse_desc"),
            icon: <Landmark className="size-5" aria-hidden="true" />,
            disabled: !available[PayinRail.PSE],
          },
          {
            value: PayinRail.NEQUI,
            label: formatRail("NEQUI"),
            description: describe(PayinRail.NEQUI, "method_nequi_desc"),
            icon: <Smartphone className="size-5" aria-hidden="true" />,
            disabled: !available[PayinRail.NEQUI],
          },
          {
            value: PayinRail.BANCOLOMBIA_BUTTON,
            label: formatRail("BANCOLOMBIA_BUTTON"),
            description: describe(PayinRail.BANCOLOMBIA_BUTTON, "method_bancolombia_desc"),
            icon: <MousePointerClick className="size-5" aria-hidden="true" />,
            disabled: !available[PayinRail.BANCOLOMBIA_BUTTON],
          },
          // Declared in full and narrowed last, so the four descriptions stay side by side: what changes
          // between surfaces is which of them are offered, not what any of them says.
        ].filter((item) => methods.includes(item.value))}
      />
    </div>
  );
}
